import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FaBell, FaTimes } from 'react-icons/fa';
import { API_ENDPOINTS } from '../config/backendConfig';
import { useAuth } from '../context/AuthContext';
import { auth } from '../firebase';
import { useNavigate } from 'react-router-dom';

const NotificationBell = ({ refreshTrigger = 0 }) => {
  const { user, isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      fetchNotifications();
      // Poll for new notifications every 30 seconds
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  // Refresh notifications when refreshTrigger changes
  useEffect(() => {
    if (user && refreshTrigger > 0) {
      console.log('NotificationBell: Refreshing notifications due to trigger', { refreshTrigger });
      fetchNotifications();
    }
  }, [refreshTrigger, user]);

  const fetchNotifications = async () => {
    if (!user) return;
    
    try {
      const idToken = await auth.currentUser?.getIdToken?.();
      const response = await fetch(`${API_ENDPOINTS.getNotifications}?userId=${user.uid}`, {
        headers: {
          ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
        },
      });
      const data = await response.json();
      if (data.success) {
        setNotifications(data.notifications);
        setUnreadCount(data.notifications.filter(n => !n.read).length);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const idToken = await auth.currentUser?.getIdToken?.();
      const response = await fetch(API_ENDPOINTS.markNotificationRead, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
        },
        body: JSON.stringify({ notificationId }),
      });

      const data = await response.json();
      if (data.success) {
        setNotifications(prev => 
          prev.map(n => 
            n.id === notificationId ? { ...n, read: true } : n
          )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    
    // Handle co-learning space notifications (comment/like on shared artworks)
    // Check if this is a mentor notification (has artworkId pointing to sharedArtworks)
    if (notification.type === 'comment' || notification.type === 'like') {
      // For mentor notifications, artworkId should be the sharedArtworks document ID
      // For artist notifications, relatedId is the artworks document ID
      if (notification.artworkId) {
        // This is likely a sharedArtworks document ID - navigate to co-learning space
        navigate(`/mentors/co-learning?artworkId=${notification.artworkId}&scope=${notification.scope || 'global'}`);
      } else if (notification.relatedId) {
        // Check if this is a shared artwork by looking up the artwork URL
        // For now, try co-learning space first, then fall back to gallery
        navigate(`/mentors/co-learning?artworkId=${notification.relatedId}&scope=${notification.scope || 'global'}`);
      } else {
        navigate('/mentors/co-learning');
      }
      setShowDropdown(false);
      return;
    }
    
    // Navigate to the artwork using relatedId (which contains the artworkId)
    if (notification.relatedId) {
      // Use the galleryType from the notification to determine which gallery to navigate to
      if (notification.galleryType === 'studio') {
        // Artmosphere Learning Space gallery
        navigate(`/gallery/studio?artworkId=${notification.relatedId}`);
      } else {
        // General Exhibit gallery (default)
        navigate(`/gallery/general?artworkId=${notification.relatedId}`);
      }
    }
    
    setShowDropdown(false);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const handleArtistNameClick = (artistId) => {
    if (artistId) {
      navigate(`/artist-public-gallery/${artistId}`);
      setShowDropdown(false);
    }
  };

  const handleArtistNameDoubleClick = (artistId) => {
    if (artistId) {
      navigate(`/artist-public-gallery/${artistId}`);
      setShowDropdown(false);
    }
  };

  // Listen for global popover close events
  useEffect(() => {
    const handler = (evt) => {
      // Ignore if we are the source (optional: check evt.detail)
      setShowDropdown(false);
    };
    window.addEventListener('ui-close-popovers', handler);
    return () => window.removeEventListener('ui-close-popovers', handler);
  }, []);

  // Close dropdown when clicking outside or on any button
  useEffect(() => {
    if (!showDropdown) return;

    const handleClickOutside = (event) => {
      // Check if click is outside the dropdown
      const dropdown = document.querySelector('.notification-dropdown');
      const bellButton = event.target.closest('.notification-bell-button');
      
      // Close if clicking outside dropdown and not on the bell button
      if (dropdown && !dropdown.contains(event.target) && !bellButton) {
        setShowDropdown(false);
      }
    };

    // Close when any button is clicked (except the bell button itself)
    const handleButtonClick = (event) => {
      const target = event.target;
      const isButton = target.tagName === 'BUTTON' || target.closest('button');
      const isBellButton = target.closest('.notification-bell-button');
      
      // Close if any button is clicked (except the bell button)
      if (isButton && !isBellButton) {
        setShowDropdown(false);
      }
    };

    // Add event listeners
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('click', handleButtonClick);
    
    // Also listen for navigation events
    const handleNavigation = () => {
      setShowDropdown(false);
    };
    window.addEventListener('popstate', handleNavigation);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('click', handleButtonClick);
      window.removeEventListener('popstate', handleNavigation);
    };
  }, [showDropdown]);

  if (!user) return null;

  return (
    <div className="relative z-[95]">
      {/* Notification Bell */}
      <button
        onClick={() => {
          // Close other popovers globally before opening this one
          window.dispatchEvent(new CustomEvent('ui-close-popovers', { detail: 'notification-bell' }));
          setShowDropdown((prev) => !prev);
        }}
        className="notification-bell-button relative p-2 text-gray-600 hover:text-gray-800 transition-colors"
      >
        <FaBell className="text-xl" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notifications Dropdown */}
      {showDropdown && createPortal(
        <div className="notification-dropdown fixed right-4 top-16 w-80 bg-white rounded-lg shadow-lg border z-[1000] max-h-96 overflow-y-auto">
          <div className="p-4 border-b">
            <div className="flex justify-between items-center">
              <h3 className="font-semibold text-gray-800">Notifications</h3>
              <button
                onClick={() => setShowDropdown(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <FaTimes />
              </button>
            </div>
          </div>
          
          <div className="p-2">
            {notifications.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FaBell className="text-3xl mx-auto mb-2 opacity-50" />
                <p>No notifications yet</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`p-3 rounded-lg cursor-pointer transition-colors ${
                    notification.read 
                      ? 'bg-gray-50 hover:bg-gray-100' 
                      : 'bg-blue-50 hover:bg-blue-100'
                  } ${!notification.read ? 'border-l-4 border-blue-500' : ''}`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="font-medium text-gray-800 text-sm">
                        {notification.title}
                      </p>
                      <p className="text-gray-600 text-sm mt-1">
                        {notification.message}
                      </p>
                      {notification.fromUserName && (
                        <p className="text-blue-600 text-xs mt-1">
                          From: {notification.fromUserId ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleArtistNameClick(notification.fromUserId);
                              }}
                              onDoubleClick={() => handleArtistNameDoubleClick(notification.fromUserId)}
                              className="hover:text-blue-800 hover:underline transition-colors"
                              title="View artist profile"
                            >
                              {notification.fromArtistName || notification.fromUserName}
                            </button>
                          ) : (
                            notification.fromArtistName || notification.fromUserName
                          )}
                        </p>
                      )}
                      {notification.artistDisplayName && notification.artistDisplayName !== notification.fromUserName && (
                        <p className="text-green-600 text-xs mt-1">
                          Artist: {notification.artistId ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleArtistNameClick(notification.artistId);
                              }}
                              onDoubleClick={() => handleArtistNameDoubleClick(notification.artistId)}
                              className="hover:text-green-800 hover:underline transition-colors"
                              title="View artist profile"
                            >
                              {notification.artistDisplayName}
                            </button>
                          ) : (
                            notification.artistDisplayName
                          )}
                        </p>
                      )}
                    </div>
                    <span className="text-xs text-gray-500 ml-2">
                      {formatTimestamp(notification.timestamp)}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>, document.body
      )}
    </div>
  );
};

export default NotificationBell; 